<template>
    <div
        class="overflow-hidden rounded-[12.8px] border border-gray-200 bg-white"
    >
        <!-- Header -->
        <div
            v-if="headerEnabled"
            class="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5"
        >
            <div>
                <h3 class="font-medium text-gray-900">
                    {{ title }}
                </h3>
                <p v-if="subtitle" class="mt-1 text-sm text-gray-600">
                    {{ subtitle }}
                </p>
            </div>

            <div
                v-if="searchEnabled"
                class="relative w-[280px] max-w-full shrink-0"
            >
                <MagnifyingGlassIcon
                    class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                />
                <input
                    v-model.trim="q"
                    type="text"
                    :placeholder="searchPlaceholder"
                    class="h-9 w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 text-[13px] text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    :aria-label="searchAriaLabel"
                    @keydown.enter.prevent="applySearchNow"
                    @input="onSearchInput"
                />
            </div>
        </div>

        <!-- Table -->
        <div class="overflow-x-auto rounded-[12.8px] rounded-t-none">
            <table
                class="w-full table-fixed [&_thead_th]:px-6 [&_thead_th]:py-0 [&_thead_th]:h-[46px] [&_thead_th]:align-middle [&_thead_th]:text-left [&_thead_th]:text-sm [&_thead_th]:font-medium [&_thead_th]:text-gray-600 [&_tbody_td]:px-6 [&_tbody_td]:py-4 [&_tbody_td]:align-middle"
            >
                <colgroup>
                    <col
                        v-for="c in normalizedColumns"
                        :key="c.key"
                        :class="c.colClass"
                    />
                    <col v-if="hasKebab" class="w-[48px]" />
                </colgroup>

                <thead class="border-b border-gray-100 bg-gray-50">
                    <tr>
                        <th
                            v-for="c in normalizedColumns"
                            :key="c.key"
                            :class="c.thClass"
                        >
                            <span
                                class="inline-flex min-w-0 items-center gap-2"
                            >
                                <button
                                    type="button"
                                    class="rounded px-1 -mx-1"
                                    :class="
                                        c.sortable && isSortedBy(c.sortKey)
                                            ? 'bg-gray-100'
                                            : ''
                                    "
                                    :disabled="!c.sortable"
                                    @click="
                                        c.sortable && setSort(c.sortKey, 'asc')
                                    "
                                >
                                    {{ c.label }}
                                </button>

                                <span
                                    v-if="c.sortable"
                                    class="inline-flex flex-col -space-y-[2px]"
                                >
                                    <button
                                        type="button"
                                        class="rounded-[12.8px] p-[1px] hover:bg-gray-100"
                                        aria-label="Sort ascending"
                                        @click.stop="setSort(c.sortKey, 'asc')"
                                    >
                                        <ChevronUpIcon
                                            class="h-3.5 w-3.5 transition-colors"
                                            :class="
                                                isActiveSort(c.sortKey, 'asc')
                                                    ? 'text-gray-700'
                                                    : 'text-gray-300 hover:text-gray-500'
                                            "
                                        />
                                    </button>

                                    <button
                                        type="button"
                                        class="rounded-[12.8px] p-[1px] hover:bg-gray-100"
                                        aria-label="Sort descending"
                                        @click.stop="setSort(c.sortKey, 'desc')"
                                    >
                                        <ChevronDownIcon
                                            class="h-3.5 w-3.5 transition-colors"
                                            :class="
                                                isActiveSort(c.sortKey, 'desc')
                                                    ? 'text-gray-700'
                                                    : 'text-gray-300 hover:text-gray-500'
                                            "
                                        />
                                    </button>
                                </span>
                            </span>
                        </th>

                        <th
                            v-if="hasKebab"
                            class="px-6 py-0 h-[46px] bg-gray-50"
                            aria-label="Actions"
                        ></th>
                    </tr>
                </thead>

                <tbody class="divide-y divide-gray-100">
                    <tr
                        v-for="row in displayRows"
                        :key="rowKeyValue(row)"
                        class="domain-row transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
                        :class="rowClickable ? 'cursor-pointer' : ''"
                        role="link"
                        tabindex="0"
                        @click="rowClickable && goToRow(row)"
                        @keydown.enter.prevent="rowClickable && goToRow(row)"
                        @keydown.space.prevent="rowClickable && goToRow(row)"
                    >
                        <!-- Default cells OR custom row slot -->
                        <template v-if="$slots.row">
                            <slot name="row" :row="row" />
                        </template>

                        <template v-else>
                            <td
                                v-for="c in normalizedColumns"
                                :key="c.key"
                                :class="c.tdClass"
                            >
                                <slot
                                    :name="`cell-${c.key}`"
                                    :row="row"
                                    :value="getValue(row, c.key)"
                                >
                                    <span class="text-gray-900">
                                        {{ getValue(row, c.key) }}
                                    </span>
                                </slot>
                            </td>
                        </template>

                        <!-- Kebab -->
                        <td v-if="hasKebab" class="px-6 py-4" @click.stop>
                            <RowKebabMenu
                                :items="resolveKebabItems(row)"
                                :row="row"
                            />
                        </td>
                    </tr>

                    <tr v-if="isEmpty">
                        <td
                            :colspan="
                                normalizedColumns.length + (hasKebab ? 1 : 0)
                            "
                            class="px-6 py-10 text-center"
                        >
                            <span class="text-sm text-gray-500">{{
                                emptyText
                            }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Footer -->
        <div
            v-if="paginationEnabled"
            class="px-6 py-4 border-t border-gray-100 flex items-center gap-4"
        >
            <p class="text-[12px] text-gray-500 min-w-0 truncate">
                {{ showingText }}
            </p>

            <div
                class="ml-auto flex items-center gap-4 min-w-0 overflow-hidden"
            >
                <div v-if="perPageEnabled" class="shrink-0">
                    <div
                        class="inline-flex h-9 overflow-hidden rounded-md border border-gray-200 bg-white"
                    >
                        <div class="relative h-9">
                            <select
                                v-model.number="perPage"
                                class="absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none bg-transparent px-2 pr-8 text-[13px] text-transparent focus:outline-none"
                                aria-label="Items per page"
                                @change="applyPerPage"
                            >
                                <option
                                    v-for="n in perPageOptions"
                                    :key="n"
                                    :value="n"
                                >
                                    {{ n }}
                                </option>
                            </select>

                            <div
                                class="pointer-events-none flex h-9 items-center gap-2 px-2 pr-8"
                            >
                                <span class="text-[13px] text-gray-700">{{
                                    perPage
                                }}</span>
                                <span class="text-[13px] text-gray-700">{{
                                    perPageLabel
                                }}</span>
                            </div>

                            <ChevronDownIcon
                                class="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                            />
                        </div>
                    </div>
                </div>

                <nav
                    class="min-w-0 flex-1 inline-flex items-center gap-1 overflow-x-auto whitespace-nowrap"
                    aria-label="Pagination"
                >
                    <button
                        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[12.8px] text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        :disabled="currentPage <= 1"
                        aria-label="Previous page"
                        @click="goToPage(currentPage - 1)"
                    >
                        <ChevronLeftIcon class="h-4 w-4" />
                    </button>

                    <button
                        v-for="p in visiblePages"
                        :key="String(p)"
                        :disabled="p === '...'"
                        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[12.8px] text-[13px]"
                        :class="
                            p === currentPage
                                ? 'bg-gray-100 font-medium text-gray-900'
                                : p === '...'
                                  ? 'text-gray-400'
                                  : 'text-gray-600 hover:bg-gray-50'
                        "
                        @click="p !== '...' && goToPage(p as number)"
                    >
                        {{ p === "..." ? "..." : p }}
                    </button>

                    <button
                        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[12.8px] text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        :disabled="currentPage >= lastPage"
                        aria-label="Next page"
                        @click="goToPage(currentPage + 1)"
                    >
                        <ChevronRightIcon class="h-4 w-4" />
                    </button>
                </nav>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { router } from "@inertiajs/vue3";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
} from "@heroicons/vue/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import { EllipsisVerticalIcon } from "@heroicons/vue/24/outline";
import RowKebabMenu from "@/components/Menu/RowKebabMenu.vue";
import { useTranslations } from "@/composables/useTranslations.ts";

const { t } = useTranslations();

type SortDir = "asc" | "desc";

export type ColumnDef = {
    key: string;
    label: string;
    sortable?: boolean;
    sortKey?: string; // default = key
    colClass?: string; // <col class="">
    thClass?: string;
    tdClass?: string;
};

export type TableState = {
    q?: string;
    sort_by?: string | null;
    sort_dir?: SortDir | null;
    per_page?: number;
    current_page?: number;
};

export type Pagination = {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
    from: number | null;
    to: number | null;
};

export type KebabItem<Row = any> = {
    label: string;
    href?: string | ((row: Row) => string);
    action?: (row: Row) => void | Promise<void>;
    disabled?: boolean | ((row: Row) => boolean);
    visible?: boolean | ((row: Row) => boolean);
    danger?: boolean;
};

const props = withDefaults(
    defineProps<{
        title: string;
        subtitle?: string;

        rows: any[];
        columns: ColumnDef[];

        /** server-side: endpoint url (string) nebo factory (query)=>string */
        endpoint?: string | ((query: Record<string, any>) => string);

        /** fixní query params (např. start_date/end_date) */
        baseQuery?: Record<string, any>;

        /** stav z backendu (jako props.table) */
        table?: TableState;

        /** stránkování z backendu (jako props.pagination) */
        pagination?: Pagination;

        /** row click */
        rowLink?: string | ((row: any) => string);

        /** kebab */
        kebabItems?: KebabItem[] | ((row: any) => KebabItem[]);

        /** search */
        searchEnabled?: boolean;
        searchPlaceholder?: string;
        searchAriaLabel?: string;
        searchDebounceMs?: number;

        /** footer labels/texts (aby šel reuse mimo i18n) */
        emptyText?: string;
        perPageLabel?: string;

        /** per page options */
        perPageOptions?: number[];

        /** inertia options */
        preserveScroll?: boolean;
        preserveState?: boolean;
        replace?: boolean;

        /** row key */
        rowKey?: string | ((row: any) => string | number);

        /** table functions configurability */
        headerEnabled?: boolean;
        paginationEnabled?: boolean;
        perPageEnabled?: boolean;
    }>(),
    {
        rows: () => [],
        columns: () => [],

        baseQuery: () => ({}),

        searchEnabled: true,
        searchPlaceholder: "Search...",
        searchAriaLabel: "Search",
        searchDebounceMs: 250,

        emptyText: "No results",
        perPageLabel: () => "per page",
        perPageOptions: () => [10, 25, 50, 100],

        preserveScroll: true,
        preserveState: true,
        replace: true,

        rowKey: "id",

        headerEnabled: true,
        paginationEnabled: true,
        perPageEnabled: true,
    },
);

const normalizedColumns = computed(() =>
    props.columns.map((c) => ({
        ...c,
        sortKey: c.sortKey ?? c.key,
        sortable: !!c.sortable,
        colClass: c.colClass ?? "",
        thClass:
            c.thClass ??
            "min-w-[140px] px-6 text-left text-sm font-medium text-gray-600 whitespace-nowrap",
        tdClass: c.tdClass ?? "px-6 py-4",
    })),
);

const rowClickable = computed(() => !!props.rowLink);
const hasKebab = computed(() => !!props.kebabItems);

const q = ref(props.table?.q ?? "");
const sortBy = ref<string | null>(props.table?.sort_by ?? null);
const sortDir = ref<SortDir | null>(props.table?.sort_dir ?? null);
const perPage = ref<number>(
    props.table?.per_page ??
        props.pagination?.per_page ??
        props.perPageOptions[0] ??
        10,
);

watch(
    () => props.table,
    (t) => {
        if (!t) return;
        q.value = t.q ?? "";
        sortBy.value = (t.sort_by ?? null) as any;
        sortDir.value = (t.sort_dir ?? null) as any;
        perPage.value = t.per_page ?? perPage.value;
    },
    { deep: true },
);

const currentPage = computed(
    () => props.pagination?.current_page ?? props.table?.current_page ?? 1,
);
const lastPage = computed(() => props.pagination?.last_page ?? 1);

const searchEnabled = computed(() => props.searchEnabled);
const searchPlaceholder = computed(() => props.searchPlaceholder);
const searchAriaLabel = computed(() => props.searchAriaLabel);

const isSortedBy = (key: string) => sortBy.value === key;
const isActiveSort = (key: string, dir: SortDir) =>
    sortBy.value === key && sortDir.value === dir;

const clearSort = () => {
    sortBy.value = null;
    sortDir.value = null;
};

const resolveEndpoint = (query: Record<string, any>) => {
    if (!props.endpoint) return null;
    return typeof props.endpoint === "function"
        ? props.endpoint(query)
        : props.endpoint;
};

const buildQuery = (overrides: Record<string, any> = {}) => {
    const data: Record<string, any> = {
        ...props.baseQuery,

        q: q.value || undefined,
        per_page: perPage.value,
        current_page: currentPage.value,

        ...overrides,
    };

    if (sortBy.value && sortDir.value) {
        data.sort_by = sortBy.value;
        data.sort_dir = sortDir.value;
    }

    return data;
};

const navigate = (overrides: Record<string, any> = {}) => {
    const query = buildQuery(overrides);
    const endpoint = resolveEndpoint(query);

    if (!endpoint) return;

    router.get(endpoint, query, {
        replace: props.replace,
        preserveScroll: props.preserveScroll,
        preserveState: props.preserveState,
    });
};

const setSort = (key: string, dir: SortDir) => {
    if (isActiveSort(key, dir)) {
        clearSort();
        navigate({ current_page: 1 });
        return;
    }
    sortBy.value = key;
    sortDir.value = dir;
    navigate({ current_page: 1 });
};

const goToPage = (page: number) => {
    if (!page || page < 1 || page > lastPage.value) return;
    navigate({ current_page: page });
};

const applyPerPage = () => {
    navigate({ per_page: perPage.value, current_page: 1 });
};

let searchTimer: number | undefined;

const applySearchNow = () => {
    navigate({ q: q.value || undefined, current_page: 1 });
};

const onSearchInput = () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
        applySearchNow();
    }, props.searchDebounceMs);
};

onBeforeUnmount(() => {
    window.clearTimeout(searchTimer);
});

/** visible pages: 1 ... cur-1 cur cur+1 ... last */
const visiblePages = computed<(number | string)[]>(() => {
    const cur = currentPage.value;
    const last = lastPage.value;

    const windowSize = 1;
    const pages: (number | string)[] = [];

    pages.push(1);

    if (cur - windowSize > 2) pages.push("...");

    const start = Math.max(2, cur - windowSize);
    const end = Math.min(last - 1, cur + windowSize);
    for (let p = start; p <= end; p++) pages.push(p);

    if (cur + windowSize < last - 1) pages.push("...");

    if (last > 1) pages.push(last);

    return pages.filter((v, i, a) => i === 0 || v !== a[i - 1]);
});

const isEmpty = computed(() => (props.rows?.length ?? 0) === 0);

const displayRows = computed(() => props.rows ?? []);

const showingText = computed(() => {
    const total = props.pagination?.total ?? displayRows.value.length;
    const from = props.pagination?.from ?? (displayRows.value.length ? 1 : 0);
    const to = props.pagination?.to ?? displayRows.value.length;

    return `${t("vue.showing")} ${from} ${t("vue.showing_to")} ${to} ${t("vue.showing_of")} ${total} ${t("vue.showing_results")}`;
});

const rowKeyValue = (row: any) => {
    if (typeof props.rowKey === "function") return props.rowKey(row);
    return row?.[props.rowKey] ?? row?.id ?? JSON.stringify(row);
};

const goToRow = (row: any) => {
    if (!props.rowLink) return;
    const href =
        typeof props.rowLink === "function"
            ? props.rowLink(row)
            : props.rowLink;
    if (!href) return;
    router.visit(href);
};

const getValue = (row: any, path: string) => {
    if (!path.includes(".")) return row?.[path];
    return path
        .split(".")
        .reduce((acc: any, key: string) => (acc ? acc[key] : undefined), row);
};

const resolveKebabItems = (row: any): KebabItem[] => {
    const items =
        typeof props.kebabItems === "function"
            ? props.kebabItems(row)
            : (props.kebabItems ?? []);
    return (items ?? [])
        .filter((it) => {
            if (typeof it.visible === "function") return it.visible(row);
            if (typeof it.visible === "boolean") return it.visible;
            return true;
        })
        .map((it) => ({
            ...it,
            disabled:
                typeof it.disabled === "function"
                    ? it.disabled(row)
                    : it.disabled,
        }));
};
</script>

<style scoped>
.domain-row {
    transition: background-color 0.12s ease;
    height: 49.75px;
}

.domain-row > td {
    height: 49.75px;
    padding-top: 6px;
    padding-bottom: 6px;
    vertical-align: middle;
}

.domain-row:hover td {
    background: #fafafa;
}
</style>
